// Session 15: publish dates for both cadence types, same rule as the DB's
// _s15_cadence_dates and tools/lotus_cadence.cadence_dates on the droplet.
//   weekly  : every day whose weekday is in publish_weekdays
//   monthly : the first publish_weekday on or after each month_day (1..28)
//             (Session 15b: no publish_weekday = the exact month_day)
// Session 15b: cadenceSlots = _s15_cadence_slots / cadence_slots: each date
// carries posts_per_date posts (monthly), plus an optional kick-off batch.
// Dates are ISO 'YYYY-MM-DD' strings; arithmetic is done in UTC so DST and
// the browser's timezone never shift a day.

export const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const toUtc = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const toIso = (dt) => dt.toISOString().slice(0, 10);
const addDaysUtc = (dt, n) => new Date(dt.getTime() + n * 86400000);
const monIndex = (dt) => (dt.getUTCDay() + 6) % 7;   // 0 = Monday

export const isMonthly = (c) => String(c?.type || 'weekly').toLowerCase() === 'monthly';

export function cadenceDates(c, fromIso, toIsoStr) {
  const from = toUtc(fromIso); const to = toUtc(toIsoStr);
  const out = new Set();
  if (!isMonthly(c)) {
    const want = new Set((c?.publish_weekdays?.length ? c.publish_weekdays : ['tue']).map((d) => WEEKDAY_KEYS.indexOf(String(d).toLowerCase().slice(0, 3))));
    for (let d = from; d <= to; d = addDaysUtc(d, 1)) if (want.has(monIndex(d))) out.add(toIso(d));
    return [...out].sort();
  }
  const exact = !String(c?.publish_weekday || '').trim();   // Session 15b
  const wd = WEEKDAY_KEYS.indexOf(String(c?.publish_weekday || '').toLowerCase().slice(0, 3));
  const days = (c?.month_days || []).map(Number).filter((n) => Number.isInteger(n) && n >= 1 && n <= 28);
  if ((wd < 0 && !exact) || !days.length) return [];
  const start = addDaysUtc(from, -31);
  for (let y = start.getUTCFullYear(), m = start.getUTCMonth(); Date.UTC(y, m, 1) <= to.getTime(); m === 11 ? (y += 1, m = 0) : (m += 1)) {
    for (const md of days) {
      const anchor = new Date(Date.UTC(y, m, md));
      const d = exact ? anchor : addDaysUtc(anchor, (wd - monIndex(anchor) + 7) % 7);
      if (d >= from && d <= to) out.add(toIso(d));
    }
  }
  return [...out].sort();
}

// Session 15b: [[iso, index], ...] in [from, to]. Monthly: posts_per_date per
// date, plus kickoff_date; regular dates before the kick-off are not used.
export function cadenceSlots(c, fromIso, toIsoStr) {
  if (!isMonthly(c)) return cadenceDates(c, fromIso, toIsoStr).map((d) => [d, 1]);
  const kick = /^\d{4}-\d{2}-\d{2}$/.test(c?.kickoff_date || '') ? c.kickoff_date : null;
  const days = new Set(cadenceDates(c, fromIso, toIsoStr).filter((d) => !kick || d >= kick));
  if (kick && kick >= fromIso && kick <= toIsoStr) days.add(kick);
  const ppd = Math.min(Math.max(Number(c?.posts_per_date) || 1, 1), 4);
  return [...days].sort().flatMap((d) => Array.from({ length: ppd }, (_, i) => [d, i + 1]));
}

// Session 15b: the next n slot dates grouped: [{date, count}, ...]
export function nextSlotGroups(c, fromIso, n) {
  const out = [];
  let from = fromIso;
  for (let guard = 0; out.length < n && guard < 12; guard += 1) {
    const to = toIso(addDaysUtc(toUtc(from), 89));
    for (const [d] of cadenceSlots(c, from, to)) {
      const last = out[out.length - 1];
      if (last && last.date === d) last.count += 1;
      else if (out.length < n) out.push({ date: d, count: 1 });
    }
    from = toIso(addDaysUtc(toUtc(to), 1));
  }
  return out;
}

export function nextDates(c, fromIso, n) {
  const out = [];
  let from = fromIso;
  for (let guard = 0; out.length < n && guard < 12; guard += 1) {
    const to = toIso(addDaysUtc(toUtc(from), 89));
    out.push(...cadenceDates(c, from, to).slice(0, n - out.length));
    from = toIso(addDaysUtc(toUtc(to), 1));
  }
  return out;
}

// 'Wed Oct 7'
const DAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const fmtShort = (iso) => { const d = toUtc(iso); return `${DAY_ABBR[monIndex(d)]} ${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}`; };
